// Copyright (c) Docugami, Inc. All rights reserved.

import { nanoid } from "@reduxjs/toolkit";
import { chatTimeoutResponseMessage } from "components/chat/ChatHelpers";
import { useEffect, useState } from "react";
import { ChatOptions, SendMessageRequest, StreamingChatMessageRequest } from "store/chat/chatApi";
import { chatApi, chatHistoryAdapter, createErrorResponse, getRequestChatHistoryContext, updateSuggestionsThunk, useSendChatMessageWithStreamingMutation, useSendMessageMutation } from "store/chat/chatSlice";
import { createSelectChatMessageResponse } from "store/chat/selectors";
import { FeatureFlags } from "store/featureFlags/types";
import { useFeatureFlag } from "./FeatureFlags";
import { useTimeout } from "./Timeout";
import { useAppDispatch, useAppSelector } from "./redux";
import { Telemetry } from "telemetry/TelemetryManager";
import { ChatResponseTimeoutSchema, TelemetryEvent } from "telemetry/TelemetryEvents";

interface SendChatMessageRequest {
    options: ChatOptions;
    message: string;
    suggestionId?: string;
}

export function useSendChatMessage(sessionId?: string) {
    const [triggerSend, data] = useSendMessageMutation();
    const { trigger: triggerSendWithStreaming, data: streamingData } = useStreamingChatMessage(sessionId);

    const dispatch = useAppDispatch();

    const sendWithStreaming = useFeatureFlag(FeatureFlags.ChatStreaming);

    function onSendMessage(arg: SendChatMessageRequest) {
        if (!!arg.suggestionId) {
            // Update chat query data if the message being sent is a suggestion
            dispatch(updateSuggestionsThunk(arg.suggestionId, arg.options));
        }

        const historyContext = dispatch(getRequestChatHistoryContext(arg.options, sessionId));
        if (sendWithStreaming) {
            triggerSendWithStreaming({
                options: arg.options,
                message: arg.message,
                chatHistory: historyContext,
                sessionId: sessionId
            });
        } else {
            const request: SendMessageRequest = {
                options: arg.options,
                message: {
                    messageId: nanoid(),
                    text: arg.message,
                    timestamp: new Date().toISOString(),
                    isFinal: false
                },
                chatHistory: historyContext
            }

            triggerSend(request);
        }
    }

    return {
        trigger: onSendMessage,
        data: sendWithStreaming ? streamingData : data
    }
}

// Fallback timeout for awaiting a message response
const responseAwaitTimeoutMs = 60 * 1000;
const selectChatMessageResponse = createSelectChatMessageResponse();
function useStreamingChatMessage(sessionId?: string) {
    const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
    const [trigger, sendMessageResponse] = useSendChatMessageWithStreamingMutation();

    const messageReply = useAppSelector(state => selectChatMessageResponse(state, sendMessageResponse.originalArgs?.options, sendMessageResponse.data?.id));

    const dispatch = useAppDispatch();

    useTimeout(() => setIsAwaitingResponse(false), isAwaitingResponse ? responseAwaitTimeoutMs : undefined);

    useEffect(() => {
        if (isAwaitingResponse && (!!messageReply?.text || sendMessageResponse.isError)) {
            setIsAwaitingResponse(false);
        }
    }, [isAwaitingResponse, messageReply, sendMessageResponse.isError])

    useEffect(() => {
        // Note: This sort of timeout update owned by the client should be ripped out when we support chat history for real
        // If we timeout while waiting for a streamed response for the sent chat message,
        // add a timeout error system message to the chat history to inform the user of the error
        if (!isAwaitingResponse && !messageReply) {
            // Update the chat history with a system error response for the sent user chat message
            if (!!sendMessageResponse.originalArgs && !!sendMessageResponse.data?.id) {
                Telemetry.trackEvent<ChatResponseTimeoutSchema>({
                    messageId: sendMessageResponse.data!.id,
                    eventName: TelemetryEvent.ChatResponseTimeout
                })

                dispatch(chatApi.util.updateQueryData("initializeDocSetChat", sendMessageResponse.originalArgs.options, x => {
                    chatHistoryAdapter.addOne(x.chatHistory, { ...createErrorResponse(sendMessageResponse.data!.id, chatTimeoutResponseMessage, 'Timeout', sessionId) })
                }));
            }
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [isAwaitingResponse])

    function triggerSend(arg: StreamingChatMessageRequest) {
        setIsAwaitingResponse(true);
        trigger(arg);
    }

    return {
        trigger: triggerSend,
        data: {
            ...sendMessageResponse,
            isLoading: sendMessageResponse.isLoading || isAwaitingResponse,
            isError: sendMessageResponse.isError
        }
    }
}
